<template>
    <div class="home">
        <b-container fluid>
            <b-row>
              <b-col>
                <h1>Application Details</h1>
              </b-col>
            </b-row>
            <b-row class="mt-2">
                <b-col cols="12">
                    <p style="font-weight:750;" class="mb-1">Notes</p>
                    <b-table hover small show-empty class="sr-caption"
                             aria-live="polite"
                             responsive="true"
                             :fields="noteFields"
                             :items="application.notes"
                             :busy="false"
                             label-sort-asc=""
                             label-sort-desc=""
                             caption="Notes table.  To sort the data in this table by a specific column,
                                go to the appropriate column header cell and use enter to sort the data ascending.
                                Use enter again to sort descending.">
                        <template v-slot:no-data>
                            No Notes Found.
                        </template>
                        <div slot="table-busy" class="text-center text-danger my-2">
                            <b-spinner class="align-middle" label="Loading, Please Wait" aria-label="Loading, Please Wait"></b-spinner>
                            <strong>Loading...</strong>
                        </div>
                        <template slot="empty" slot-scope="scope">
                            No Notes Found.
                        </template>
                    </b-table>
                </b-col>
            </b-row>

            <div class="card">
              <div class="card-header">Application Review</div>
              <div class="card-body">
                <b-row>
                    <b-col cols="2">
                        <p class="mb-1"><span class="font-weight-bold">Application Reopened: </span>
                            {{application.edited && application.returnForReview ? "Reopened" : application.edited && !application.returnForReview ? "Resubmitted" : ""}}</p>
                    </b-col>
                </b-row>
                <b-row>
                  <b-col cols="12">
                    <BaseParagraphInput v-model="comments" 
                        :v="$v.comments"
                        label="Internal Review Comments"
                        slug="reviewComments"
                        tip=""
                        setId="reviewComments"/>
                  </b-col>
                </b-row>
                  
                  <b-row>
                    <b-col cols="12">
                        <b-button variant="link" class="mr-4" @click="save">Save</b-button>
                    </b-col>
                  </b-row>
              </div>
            </div>

            <div class="card">
              <div class="card-header">Applicant Information</div>
                <div class="card-body">
                  <b-row class="mb-3">
                      <b-col cols="3">
                          <p class="mb-1"><span class="font-weight-bold">Application Status: </span></p>
                              {{application.applicationStatus}}
                      </b-col>
                      <b-col cols="3" v-if="application.applicationStatus === 'WITHDRAW'">
                          <p class="mb-1"><span class="font-weight-bold">Withdraw Date: </span></p>
                              {{application.statusDate}}
                      </b-col>
                      <b-col cols="3">
                          <p class="mb-1"><span class="font-weight-bold">I&E Status: </span></p>
                          {{application.screeningStatus}}
                      </b-col>
                      <b-col cols="3">
                          <p class="mb-1"><span class="font-weight-bold">Payment Status: </span></p>
                          <b-link :to="{ name: 'transactions', params: { id: application.applicationId } }" target="tx">{{application.paymentStatus}}</b-link>
                      </b-col>
                  </b-row>
                  <b-row>
                      <b-col cols="4"><BaseTextOutput label="Last Name" :value="application.lastName"/></b-col>
                      <b-col cols="4"><BaseTextOutput label="First Name" :value="application.firstName"/></b-col>
                      <b-col cols="4"><BaseTextOutput label="Middle Name" :value="application.middleName"/></b-col>
                  </b-row>

                  <Address :value="application.address" :international="application.international"/>

                  <b-row class="mt-3">
                      <b-col cols="4"><BaseTextOutput label="Preferred Phone" :value="application.preferredPhone"/></b-col>
                      <b-col cols="4"><BaseTextOutput label="Email Address" :value="application.emailAddress"/></b-col>
                  </b-row>

                  <b-row>
                      <b-col cols="4"><BaseTextOutput label="License Number" :value="application.license.licenseNbr"/></b-col>
                      <b-col cols="4"><BaseTextOutput label="Effective Date" :value="!!application.license.effectiveDate
                      ? momentFormat(application.license.effectiveDate) : ''"/></b-col>
                      <b-col cols="4"><BaseTextOutput label="Expiration Date" :value="!!application.license.expirationDate ?
                      momentFormat(application.license.expirationDate) : ''"/></b-col>
                  </b-row>

                  <h2 class="mt-3">Other Names</h2>
                  <OtherNames :value="application.otherNames"/>

                  <h2 class="mt-3">Trade Names</h2>
                  <TradeNames :value="application.tradeNames"/>

                </div>
            </div>

            <b-button variant="secondary" size="sm" :to="{name: 'appHistory', params: { id: application.applicationId }}" role="button"
                      v-if="hasHistory()">View Application History</b-button>
        </b-container>
    </div>
</template>

<script>
import axios from 'axios'
import { required, maxLength } from 'vuelidate/lib/validators'
import Address from '@/components/templates/Address.vue'
import OtherNames from '@/components/templates/OtherNames.vue'
import TradeNames from '@/components/templates/TradeNames.vue'
import moment from 'moment'

export default {
  components: {
    Address,
    OtherNames,
    TradeNames,
  },

  data: () => ({
    application: {},
    comments: null,
    noteFields: [
        {key: 'note', sortable: true, label: 'Note'},
        {key: 'noteType.description', sortable: true, label: 'Note Type'},
        {key: 'noteDate', sortable: true, label: 'Note Date'},
        {key: 'noteByName', sortable: true, label: 'Note By'}
    ],
  }),
  
  mounted() {
    this.loadApplication()
  },

  validations: {
    comments: { required: false, maxLength: maxLength(4000) }
  },

  methods: {
    loadApplication() {
      axios.get(`${process.env.VUE_APP_API_BASE_URL}/application/${this.$route.params.id}`)
        .then(response => {
          this.application = response.data
        })
    },


    save() {
      const payload = {
        applicationId: this.$route.params.id,
        applicationNotes: this.comments
      }

      axios.post(`${process.env.VUE_APP_API_BASE_URL}/screenings/save/finance`, payload)
        .then(response => {
            this.showRootToast("Application Updates", "The comments have been updated.", "success")
            if (!this.application.notes) {
                this.application.notes = []
            }
            this.application.notes.push({
                noteType: {
                    description: "Financial Staff"
                },
                note: this.comments,
                noteDate: moment().format('YYYY-MM-DD'),
                noteByName: "Me"
            })
            this.comments = null
      })
    },

    showRootToast(title, message, type) {
      this.$root.$bvToast.toast(message, {
            title: title,
            toaster: 'b-toaster-top-center',
            solid: true,
            variant: type,
            appendToast: true,
            noAutoHide:true
          })
        this.toastFocus()
    },
    toastFocus() {
        setTimeout(() => {
            let elem = document.getElementsByClassName("toast")
            elem = elem[elem.length-1]
            elem.focus()
        }, 100)
    },
    hasHistory() {
        return (this.application.prevAddress != null && this.application.prevAddress.length > 0)
            || (this.application.prevContacts != null && this.application.prevContacts.length > 0)
    },
    momentFormat(date) {
        return moment(date).format("MM/DD/YYYY")
    },
  },
  created() {
      document.title = "Finance Applicant Screening - Body Art"
  }
}
</script>

<style>
</style>